import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ModuleNav, { MODULE_NAVS } from '../../components/ModuleNav';
import { Alert, Button, Input, Modal, Popconfirm, Result, Space, Table, Tag, Typography, notification } from 'antd';
import { PlusOutlined, ReloadOutlined, SearchOutlined, ShopOutlined } from '@ant-design/icons';
import lib from '../../assets/utils/lib';
import { usePermissions } from '../../assets/utils/usePermissions';

const { Title, Paragraph } = Typography;

const FacilityTypesPage = () => {
  const { hasAnyPermission } = usePermissions();
  const allowed = useMemo(
    () => hasAnyPermission(['admin.*', 'admin.system_config']),
    [hasAnyPermission]
  );

  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [conflictMessage, setConflictMessage] = useState('');

  const [openCreate, setOpenCreate] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createError, setCreateError] = useState('');

  const [editType, setEditType] = useState(null);
  const [editName, setEditName] = useState('');
  const [editError, setEditError] = useState('');

  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await lib.getAdminFacilityTypes();
      if (resp?.status === 'error' || resp?.data?.error || resp?.error) {
        const msg = resp?.msg || resp?.data?.message || 'Unable to load facility types';
        setConflictMessage(msg);
        notification.error({ message: msg, placement: 'topLeft' });
        setTypes([]);
        return;
      }
      setConflictMessage('');
      const list = Array.isArray(resp?.data?.data) ? resp.data.data : [];
      setTypes(list);
    } catch (error) {
      const msg = error?.message || 'Unable to load facility types';
      setConflictMessage(msg);
      notification.error({ message: msg, placement: 'topLeft' });
      setTypes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (allowed) refresh();
  }, [allowed, refresh]);

  const filteredTypes = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return types;
    return types.filter((t) => (t?.name || '').toLowerCase().includes(query));
  }, [types, search]);

  const openCreateModal = () => {
    setCreateError('');
    setCreateName('');
    setOpenCreate(true);
  };

  const submitCreate = async () => {
    if (!createName.trim()) {
      setCreateError('Name is required.');
      return;
    }
    setSaving(true);
    setCreateError('');
    try {
      const resp = await lib.createAdminFacilityType({ name: createName.trim() });
      if (resp?.status === 'error' || resp?.data?.error || resp?.error) {
        const msg = resp?.msg || resp?.data?.message || 'Unable to create facility type';
        setCreateError(msg);
        return;
      }
      notification.success({ message: resp?.data?.message || 'Facility type created successfully', placement: 'topLeft' });
      setOpenCreate(false);
      refresh();
    } catch (error) {
      setCreateError(error?.response?.data?.message || error?.message || 'Unable to create facility type');
    } finally {
      setSaving(false);
    }
  };

  const openEditModal = (type) => {
    setEditError('');
    setEditType(type);
    setEditName(type?.name || '');
  };

  const submitEdit = async () => {
    if (!editType?.id) return;
    if (!editName.trim()) {
      setEditError('Name is required.');
      return;
    }
    setSaving(true);
    setEditError('');
    try {
      const resp = await lib.updateAdminFacilityType(editType.id, { name: editName.trim() });
      if (resp?.status === 'error' || resp?.data?.error || resp?.error) {
        const msg = resp?.msg || resp?.data?.message || 'Unable to update facility type';
        setEditError(msg);
        return;
      }
      notification.success({ message: resp?.data?.message || 'Facility type updated successfully', placement: 'topLeft' });
      setEditType(null);
      refresh();
    } catch (error) {
      setEditError(error?.response?.data?.message || error?.message || 'Unable to update facility type');
    } finally {
      setSaving(false);
    }
  };

  const deleteType = async (type) => {
    try {
      setSaving(true);
      const resp = await lib.deleteAdminFacilityType(type?.id);
      if (resp?.status === 'error' || resp?.data?.error || resp?.error) {
        const msg = resp?.msg || resp?.data?.message || 'Unable to delete facility type';
        notification.error({ message: msg, placement: 'topLeft' });
        return;
      }
      notification.success({ message: resp?.data?.message || 'Facility type deleted successfully', placement: 'topLeft' });
      refresh();
    } catch (error) {
      notification.error({ message: error?.message || 'Unable to delete facility type', placement: 'topLeft' });
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    {
      title: 'Facilities',
      dataIndex: 'facility_count',
      key: 'facility_count',
      width: 120,
      render: (value) => Number(value || 0),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => openEditModal(record)}>Edit</Button>
          <Popconfirm
            title={`Delete ${record?.name}?`}
            description="Only possible when no facilities or check-in type policies still reference it."
            okText="Delete"
            cancelText="Cancel"
            onConfirm={() => deleteType(record)}
          >
            <Button danger size="small" disabled={Number(record?.facility_count || 0) > 0}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (!allowed) {
    return (
      <Result
        status="403"
        title="Admin only"
        subTitle="Managing facility types requires admin.* or admin.system_config."
      />
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <ShopOutlined style={{ fontSize: 20 }} />
        <Title level={3} style={{ margin: 0 }}>Facility types</Title>
      </div>
      <ModuleNav items={MODULE_NAVS.facilities} />
      <Paragraph type="secondary" style={{ marginBottom: 20 }}>
        Categories used to classify facilities during onboarding and in filters (Hospital, Clinic, Pharmacy, etc).
      </Paragraph>

      {conflictMessage ? (
        <Alert type="error" showIcon message={conflictMessage} style={{ marginBottom: 12 }} closable onClose={() => setConflictMessage('')} />
      ) : null}

      <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }}>
        <Input
          placeholder="Search facility types…"
          prefix={<SearchOutlined />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
          style={{ width: 280 }}
        />
        <Space>
          <Button icon={<ReloadOutlined />} onClick={refresh} loading={loading}>Refresh</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>New facility type</Button>
        </Space>
      </Space>

      <Table
        columns={columns}
        dataSource={filteredTypes}
        loading={loading}
        rowKey={(row) => String(row?.id)}
        size="small"
        pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (total) => `${total} facility types` }}
      />

      <Modal
        open={openCreate}
        title="Create facility type"
        onCancel={() => setOpenCreate(false)}
        onOk={submitCreate}
        okText="Create"
        confirmLoading={saving}
      >
        {createError ? <Alert type="error" showIcon message={createError} style={{ marginBottom: 12 }} /> : null}
        <Input
          placeholder="e.g. Diagnostic Centre"
          value={createName}
          onChange={(e) => setCreateName(e.target.value)}
          onPressEnter={submitCreate}
        />
      </Modal>

      <Modal
        open={!!editType}
        title={`Edit facility type${editType?.name ? ` — ${editType.name}` : ''}`}
        onCancel={() => setEditType(null)}
        onOk={submitEdit}
        okText="Save"
        confirmLoading={saving}
      >
        {editError ? <Alert type="error" showIcon message={editError} style={{ marginBottom: 12 }} /> : null}
        <Input
          value={editName}
          onChange={(e) => setEditName(e.target.value)}
          onPressEnter={submitEdit}
        />
        {Number(editType?.facility_count || 0) > 0 ? (
          <Tag style={{ marginTop: 10 }}>
            Renaming updates {editType.facility_count} existing facilit{Number(editType.facility_count) === 1 ? 'y' : 'ies'} of this type.
          </Tag>
        ) : null}
      </Modal>
    </div>
  );
};

export default FacilityTypesPage;
